/**
 * Book Detail Page - FLO SIGNATURE DESIGN SYSTEM v2
 * Clean. Controlled. Consistent. Light ops mode.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { apiClient } from '../api/apiConfig';
import { Book, Chapter } from '../types/book';
import { ArrowPathIcon, ExclamationTriangleIcon, PencilIcon, TrashIcon, PlusIcon, ArrowLeftIcon, ChevronDownIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import BookForm from '../components/forms/BookForm';
import ChapterForm from '../components/forms/ChapterForm';
import { COLORS, SPACING, TYPOGRAPHY, RADIUS } from '../design/tokens';
import { Panel, Button, Badge, Divider } from '../design/primitives';

const BookDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [book, setBook] = useState<Book | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isChapterModalOpen, setIsChapterModalOpen] = useState(false);
  const [selectedChapter, setSelectedChapter] = useState<Chapter | undefined>(undefined);
  const [expandedChapters, setExpandedChapters] = useState<Set<number>>(new Set());
  const [allExpanded, setAllExpanded] = useState(false);

  const fetchBook = useCallback(async () => {
    if (!id) return;
    try {
      setLoading(true);
      const response = await apiClient.get(`/api/books/${id}/`);
      setBook(response.data);
    } catch (error) {
      console.error('Error fetching book:', error);
      setError('Failed to load book details');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchBook();
  }, [id, fetchBook]);

  const handleEditBook = () => {
    setIsEditModalOpen(true);
  };

  const handleDeleteBook = async () => {
    if (!window.confirm('Are you sure you want to delete this book?')) {
      return;
    }

    try {
      await apiClient.delete(`/api/books/${id}/`);
      navigate('/learning');
    } catch (error) {
      console.error('Error deleting book:', error);
      alert('Failed to delete book');
    }
  };

  const handleAddChapter = () => {
    setSelectedChapter(undefined);
    setIsChapterModalOpen(true);
  };

  const handleEditChapter = (chapter: Chapter) => {
    setSelectedChapter(chapter);
    setIsChapterModalOpen(true);
  };

  const handleDeleteChapter = async (chapterId: number) => {
    if (!window.confirm('Are you sure you want to delete this chapter?')) {
      return;
    }

    try {
      await apiClient.delete(`/api/chapters/${chapterId}/`);
      fetchBook();
    } catch (error) {
      console.error('Error deleting chapter:', error);
      alert('Failed to delete chapter');
    }
  };

  const toggleChapterExpansion = (chapterId: number) => {
    setExpandedChapters(prev => {
      const newSet = new Set(prev);
      if (newSet.has(chapterId)) {
        newSet.delete(chapterId);
      } else {
        newSet.add(chapterId);
      }
      return newSet;
    });
  };

  const toggleAllChapters = () => {
    if (allExpanded) {
      setExpandedChapters(new Set());
      setAllExpanded(false);
    } else {
      const allChapterIds = book?.chapters?.map(chapter => chapter.id) || [];
      setExpandedChapters(new Set(allChapterIds));
      setAllExpanded(true);
    }
  };

  const getStatusBadgeColor = (status: string): 'success' | 'accent' | 'default' => {
    switch (status) {
      case 'read_and_digested':
      case 'read':
        return 'success';
      case 'in_progress':
        return 'accent';
      default:
        return 'default';
    }
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
        <ArrowPathIcon style={{ width: '32px', height: '32px', color: COLORS.accent.default, animation: 'spin 1s linear infinite' }} />
      </div>
    );
  }

  if (error || !book) {
    return (
      <Panel style={{ padding: SPACING[16] }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: SPACING[12] }}>
          <ExclamationTriangleIcon style={{ width: '20px', height: '20px', color: COLORS.status.error, flexShrink: 0 }} />
          <h3 style={{
            fontFamily: TYPOGRAPHY.body.fontFamily,
            fontSize: TYPOGRAPHY.body.sizes.default,
            fontWeight: '500',
            color: COLORS.status.error,
            margin: 0
          }}>
            {error || 'Book not found'}
          </h3>
        </div>
      </Panel>
    );
  }

  return (
    <div style={{
      backgroundColor: COLORS.bg.base,
      minHeight: '100vh',
      padding: SPACING[24]
    }}>
      <div style={{ maxWidth: '1024px', margin: '0 auto' }}>
        {/* Back button */}
        <button
          onClick={() => navigate('/learning')}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: SPACING[4],
            background: 'none',
            border: 'none',
            color: COLORS.text.secondary,
            fontFamily: TYPOGRAPHY.body.fontFamily,
            fontSize: TYPOGRAPHY.body.sizes.default,
            cursor: 'pointer',
            marginBottom: SPACING[24],
            padding: 0
          }}
          onMouseEnter={(e) => e.currentTarget.style.color = COLORS.text.primary}
          onMouseLeave={(e) => e.currentTarget.style.color = COLORS.text.secondary}
        >
          <ArrowLeftIcon style={{ width: '16px', height: '16px' }} />
          Back to Learning
        </button>

        {/* Book header */}
        <Panel style={{ marginBottom: SPACING[32] }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: SPACING[16] }}>
            <div style={{ flex: 1 }}>
              <h1 style={{
                fontFamily: TYPOGRAPHY.headings.fontFamily,
                fontSize: TYPOGRAPHY.headings.sizes.h1,
                fontWeight: TYPOGRAPHY.headings.weight,
                color: COLORS.text.primary,
                margin: 0,
                marginBottom: SPACING[8]
              }}>
                {book.title}
              </h1>
              <p style={{
                fontFamily: TYPOGRAPHY.body.fontFamily,
                fontSize: TYPOGRAPHY.headings.sizes.h3,
                color: COLORS.text.secondary,
                margin: 0
              }}>
                {book.author}
              </p>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: SPACING[12] }}>
              <Badge color={getStatusBadgeColor(book.status)}>
                {book.status.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
              </Badge>
              <button
                onClick={handleEditBook}
                style={{
                  padding: SPACING[8],
                  borderRadius: '50%',
                  border: 'none',
                  background: 'transparent',
                  cursor: 'pointer',
                  transition: 'background-color 0.1s ease'
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = COLORS.bg.hover}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
              >
                <PencilIcon style={{ width: '20px', height: '20px', color: COLORS.text.secondary }} />
              </button>
              <button
                onClick={handleDeleteBook}
                style={{
                  padding: SPACING[8],
                  borderRadius: '50%',
                  border: 'none',
                  background: 'transparent',
                  cursor: 'pointer',
                  transition: 'background-color 0.1s ease'
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = COLORS.bg.hover}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
              >
                <TrashIcon style={{ width: '20px', height: '20px', color: COLORS.status.error }} />
              </button>
            </div>
          </div>

          {book.overall_summary && (
            <>
              <Divider />
              <div>
                <h2 style={{
                  fontFamily: TYPOGRAPHY.headings.fontFamily,
                  fontSize: TYPOGRAPHY.headings.sizes.h3,
                  fontWeight: TYPOGRAPHY.headings.weight,
                  color: COLORS.text.primary,
                  margin: 0,
                  marginBottom: SPACING[12]
                }}>
                  Overall Summary
                </h2>
                <p style={{
                  fontFamily: TYPOGRAPHY.body.fontFamily,
                  fontSize: TYPOGRAPHY.body.sizes.default,
                  color: COLORS.text.secondary,
                  margin: 0,
                  lineHeight: '1.6'
                }}>
                  {book.overall_summary}
                </p>
              </div>
            </>
          )}
        </Panel>

        {/* Chapters section */}
        <div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: SPACING[24] }}>
            <h2 style={{
              fontFamily: TYPOGRAPHY.headings.fontFamily,
              fontSize: TYPOGRAPHY.headings.sizes.h2,
              fontWeight: TYPOGRAPHY.headings.weight,
              color: COLORS.text.primary,
              margin: 0
            }}>
              Chapters
            </h2>
            <div style={{ display: 'flex', alignItems: 'center', gap: SPACING[12] }}>
              {book.chapters && book.chapters.length > 0 && (
                <Button
                  variant="secondary"
                  onClick={toggleAllChapters}
                  icon={allExpanded ? <ChevronRightIcon style={{ width: '16px', height: '16px' }} /> : <ChevronDownIcon style={{ width: '16px', height: '16px' }} />}
                >
                  {allExpanded ? 'Collapse All' : 'Expand All'}
                </Button>
              )}
              <Button
                variant="primary"
                onClick={handleAddChapter}
                icon={<PlusIcon style={{ width: '20px', height: '20px' }} />}
              >
                Add Chapter
              </Button>
            </div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: SPACING[16] }}>
            {book.chapters && book.chapters.length > 0 ? (
              book.chapters.map((chapter) => {
                const isExpanded = expandedChapters.has(chapter.id);
                return (
                  <Panel key={chapter.id} noPadding>
                    <div style={{ padding: SPACING[16] }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <button
                          onClick={() => toggleChapterExpansion(chapter.id)}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: SPACING[8],
                            flex: 1,
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            padding: SPACING[8],
                            margin: `-${SPACING[8]}`,
                            borderRadius: RADIUS,
                            textAlign: 'left',
                            transition: 'background-color 0.1s ease'
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = COLORS.bg.hover}
                          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                        >
                          {isExpanded ? (
                            <ChevronDownIcon style={{ width: '20px', height: '20px', color: COLORS.text.tertiary, flexShrink: 0 }} />
                          ) : (
                            <ChevronRightIcon style={{ width: '20px', height: '20px', color: COLORS.text.tertiary, flexShrink: 0 }} />
                          )}
                          <h3 style={{
                            fontFamily: TYPOGRAPHY.headings.fontFamily,
                            fontSize: TYPOGRAPHY.headings.sizes.h3,
                            fontWeight: TYPOGRAPHY.headings.weight,
                            color: COLORS.text.primary,
                            margin: 0
                          }}>
                            Chapter {chapter.chapter_number}: {chapter.title}
                          </h3>
                        </button>
                        <div style={{ display: 'flex', alignItems: 'center', gap: SPACING[8], marginLeft: SPACING[16] }}>
                          {chapter.is_completed && (
                            <Badge color="success">Completed</Badge>
                          )}
                          <button
                            onClick={() => handleEditChapter(chapter)}
                            style={{
                              padding: SPACING[4],
                              borderRadius: '50%',
                              border: 'none',
                              background: 'transparent',
                              cursor: 'pointer',
                              transition: 'background-color 0.1s ease'
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = COLORS.bg.hover}
                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                          >
                            <PencilIcon style={{ width: '16px', height: '16px', color: COLORS.text.secondary }} />
                          </button>
                          <button
                            onClick={() => handleDeleteChapter(chapter.id)}
                            style={{
                              padding: SPACING[4],
                              borderRadius: '50%',
                              border: 'none',
                              background: 'transparent',
                              cursor: 'pointer',
                              transition: 'background-color 0.1s ease'
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = COLORS.bg.hover}
                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                          >
                            <TrashIcon style={{ width: '16px', height: '16px', color: COLORS.status.error }} />
                          </button>
                        </div>
                      </div>

                      {isExpanded && (
                        <div style={{ marginTop: SPACING[16], marginLeft: '28px' }}>
                          {chapter.summary && (
                            <div style={{ marginBottom: SPACING[16] }}>
                              <h4 style={{
                                fontFamily: TYPOGRAPHY.body.fontFamily,
                                fontSize: TYPOGRAPHY.body.sizes.default,
                                fontWeight: '600',
                                color: COLORS.text.primary,
                                margin: 0,
                                marginBottom: SPACING[8]
                              }}>
                                Summary
                              </h4>
                              <pre style={{
                                fontFamily: TYPOGRAPHY.body.fontFamily,
                                fontSize: TYPOGRAPHY.body.sizes.default,
                                color: COLORS.text.secondary,
                                whiteSpace: 'pre-wrap',
                                backgroundColor: COLORS.bg.base,
                                padding: SPACING[12],
                                borderRadius: RADIUS,
                                margin: 0
                              }}>
                                {chapter.summary}
                              </pre>
                            </div>
                          )}

                          {chapter.personal_notes && (
                            <div>
                              <h4 style={{
                                fontFamily: TYPOGRAPHY.body.fontFamily,
                                fontSize: TYPOGRAPHY.body.sizes.default,
                                fontWeight: '600',
                                color: COLORS.text.primary,
                                margin: 0,
                                marginBottom: SPACING[8]
                              }}>
                                Personal Notes
                              </h4>
                              <pre style={{
                                fontFamily: TYPOGRAPHY.body.fontFamily,
                                fontSize: TYPOGRAPHY.body.sizes.default,
                                color: COLORS.text.secondary,
                                whiteSpace: 'pre-wrap',
                                backgroundColor: 'rgba(59, 130, 246, 0.05)',
                                padding: SPACING[12],
                                borderRadius: RADIUS,
                                margin: 0
                              }}>
                                {chapter.personal_notes}
                              </pre>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </Panel>
                );
              })
            ) : (
              <Panel style={{ textAlign: 'center', padding: SPACING[48] }}>
                <p style={{
                  fontFamily: TYPOGRAPHY.body.fontFamily,
                  fontSize: TYPOGRAPHY.body.sizes.default,
                  color: COLORS.text.secondary,
                  margin: 0,
                  marginBottom: SPACING[16]
                }}>
                  No chapters added yet.
                </p>
                <Button
                  variant="primary"
                  onClick={handleAddChapter}
                  icon={<PlusIcon style={{ width: '20px', height: '20px' }} />}
                >
                  Add Your First Chapter
                </Button>
              </Panel>
            )}
          </div>
        </div>

        <BookForm
          isOpen={isEditModalOpen}
          onClose={() => setIsEditModalOpen(false)}
          onBookCreated={fetchBook}
          initialBook={book}
          isEditMode={true}
        />

        <ChapterForm
          isOpen={isChapterModalOpen}
          onClose={() => {
            setIsChapterModalOpen(false);
            setSelectedChapter(undefined);
          }}
          onChapterCreated={fetchBook}
          bookId={book.id}
          initialChapter={selectedChapter}
          isEditMode={!!selectedChapter}
        />
      </div>
    </div>
  );
};

export default BookDetailPage;
